import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useSyncExternalStore } from "react";
import { m } from "../../../paraglide/messages.js";
import { brandQueries } from "./brand.queries";
import {
  type BrandLens,
  chosenLens,
  inLens,
  type LensBrand,
  type LensValue,
  lensBrandIds,
  lensValueOf,
  parseStoredLens,
} from "./brand-lens";

// The lens of the brand in the screens (ADR-306): the URL's `brand`, else the choice remembered in this browser. One
// small store over localStorage, so every screen reading it draws again when it changes, here or in another tab; a
// browser that keeps nothing (a private window) still has the lens of the URL.

const STORAGE_KEY = "nexxt.guestlist.brand";
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function remember(value: LensValue) {
  try {
    if (localStorage.getItem(STORAGE_KEY) === value) return;
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    return;
  }
  for (const listener of listeners) listener();
}

/** The search of the page on display with another lens, typed against no route: every route of the shell has it. */
const withLens = (brand: LensValue) =>
  ((prev: Record<string, unknown>) => ({ ...prev, brand, page: undefined })) as never;

export interface BrandLensState {
  lens: BrandLens;
  /**
   * False until the lens is known: "My brands" waits for the crews, a default for whether there is one. The screens
   * draw their skeleton meanwhile, so a list never shows the brands of a lens about to change.
   */
  ready: boolean;
  value: LensValue;
  /** The brands with whether the user is in their crew, once read. */
  brands: readonly (LensBrand & { name: string })[];
  /** What crm narrows a read to: undefined for every brand, empty for "My brands" of a person in no crew. */
  brandIds: string[] | undefined;
  /** The name of the lens, as the chip and the empty lists say it. */
  label: string;
  /** Whether something of this brand (null for Tomorrow) is in view. */
  shows: (brandOrgId: string | null) => boolean;
  /** Another lens, in the URL of this page and remembered for the next; a list starts again from its first page. */
  choose: (value: LensValue) => void;
}

export function useBrandLens(): BrandLensState {
  const { brand: url } = useSearch({ strict: false });
  const stored = parseStoredLens(useSyncExternalStore(subscribe, readStored));
  const { data, isError } = useQuery(brandQueries.list());
  const brands = data?.items ?? [];
  const lens = chosenLens(url, stored, brands);
  const navigate = useNavigate();

  // a lens that came with a link is the one of the next visit too
  useEffect(() => {
    if (url) remember(url);
  }, [url]);

  const label =
    lens.kind === "all"
      ? m.lens_all()
      : lens.kind === "mine"
        ? m.lens_mine()
        : (brands.find((b) => b.id === lens.brandOrgId)?.name ?? m.brand_unknown());

  return {
    lens,
    // a failed read of the brands settles the lens too (no crew is known): the screens show, never wait for good
    ready:
      data !== undefined || isError || lens.kind === "brand" || (lens.kind === "all" && (url ?? stored) !== undefined),
    value: lensValueOf(lens),
    brands,
    brandIds: lensBrandIds(lens, brands),
    label,
    shows: (brandOrgId) => inLens(lens, brandOrgId, brands),
    choose: (value) => {
      remember(value);
      void navigate({ to: ".", search: withLens(value), replace: true });
    },
  };
}
